import React, {FC} from 'react';
import styled from 'styled-components';

import {HandlerOf, RendererOf} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {fontSizes, fontWeights} from '../../../../../libs/shared/ui/src/styles/fontStyles';
import {getMetricLabels} from '../../../helpers/metricLabels';
import {AnalyticsComputedProgressMetric} from '../../../transformers/analyticsMetricsTransformers';
import {AnalyticsComputedProgressMetricsEnum} from '../../../transformers/analyticsMetricsTransformersTypes';
import {ProgressBar} from '../../charts/components/progressBar/progressBar';
import {StyledElementWrapper} from '../../reportViewer/reportCommons';
import {ReportElementProps, ReportElementWrapper, ReportElementWrapperProps} from '../reportElement';
import {ChartProps} from './chartsModel';
import {MetricErrorState} from './common/metricErrorState';
import {useProgressMetricData} from './useProgressMetricData';

/*
 * Const.
 */

const defaultLabelRenderers = [
  (val: number) => <StyledFirstLabelDiv>{val}%</StyledFirstLabelDiv>,
  (val: number) => <StyledSecondLabelDiv>{val}%</StyledSecondLabelDiv>,
];

/*
 * Props.
 */

export interface ReportProgressBarProps
  extends ChartProps,
    ReportElementWrapperProps,
    ReportElementProps<AnalyticsComputedProgressMetricsEnum> {
  onSerieSelect: HandlerOf<number>;
  hideEmptyValues?: boolean;
  labelRenderers?: Array<RendererOf<number>>;
  legendRenderers?: Array<RendererOf<number>>;
}

/*
 * Styles.
 */

const StyledProgressBarWrapper = styled.div`
  margin-top: 13px;
`;

const StyledLabelDiv = styled.div`
  font-size: ${fontSizes.verySmall};
  line-height: ${fontSizes.verySmall};
  font-weight: ${fontWeights.bold};
  white-space: nowrap;
`;

const StyledFirstLabelDiv = styled(StyledLabelDiv)`
  color: ${(p) => p.theme.greys.shade90};
`;

const StyledSecondLabelDiv = styled(StyledLabelDiv)`
  color: ${(p) => p.theme.greys.white};
`;

/*
 * Component.
 */

export const ReportProgressBar: FC<ReportProgressBarProps> = ({
  reportSectionId,
  isLoading,
  height = 33,
  metric,
  onSerieSelect,
  hideEmptyValues = true,
  subtitle,
  labelRenderers = defaultLabelRenderers,
  legendRenderers,
}) => {
  const [progressData, progressDataError] = useProgressMetricData(
    metric,
    (metricData?: AnalyticsComputedProgressMetric) =>
      isLoading && (!metricData || metricData.series?.length === 0),
    [],
  );

  const {label, tooltip, isNew, isBeta} = getMetricLabels(metric);
  const title = label;
  const infoTooltip = tooltip;

  if (progressData) {
    progressData.series = progressData.series.map((serie, i) => ({
      ...serie,
      labelRenderer: labelRenderers[i],
      legendRenderer: legendRenderers?.[i],
    }));
  }

  const renderChart = () => {
    if (progressDataError) {
      return <MetricErrorState error={progressDataError} />;
    }

    return (
      <ProgressBar
        isLoading={isLoading}
        height={height}
        series={progressData?.series ?? []}
        hideEmptyValues={hideEmptyValues}
        onClick={onSerieSelect}
      />
    );
  };

  return (
    <ReportElementWrapper
      title={title}
      subtitle={subtitle}
      infoTooltip={infoTooltip}
      reportSectionId={reportSectionId}
      isNew={isNew}
      isBeta={isBeta}
    >
      <StyledElementWrapper>
        <StyledProgressBarWrapper>{renderChart()}</StyledProgressBarWrapper>
      </StyledElementWrapper>
    </ReportElementWrapper>
  );
};
